import React from 'react';
import * as HeroIcons from '@heroicons/react/24/outline';

const shouldLogIconDebug = () => (
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  window.localStorage.getItem('swan_enable_dev_tools') === 'true'
);

// Get all icon components dynamically
const getAllIcons = () => {
  const icons = {};

  if (shouldLogIconDebug()) {
    console.log('[iconMapping] Raw HeroIcons import:', Object.keys(HeroIcons).length, 'keys');
    console.log('[iconMapping] First few keys:', Object.keys(HeroIcons).slice(0, 10));
  }

  // Filter only actual icon components (modern React exports them as objects)
  Object.entries(HeroIcons).forEach(([name, component]) => {
    if (name.endsWith('Icon') && (typeof component === 'function' || typeof component === 'object')) {
      icons[name] = component;
    }
  });

  if (shouldLogIconDebug()) {
    console.log('[iconMapping] Filtered icons:', Object.keys(icons).length);
  }
  
  // Fallback: If no icons were found, manually add some common ones
  if (Object.keys(icons).length === 0) {
    console.warn('[iconMapping] No icons found through dynamic import, trying individual imports');
    try {
      // Try to import specific icons as fallback
      const { 
        Bars3Icon, 
        PencilIcon, 
        TrashIcon, 
        EyeIcon, 
        EyeSlashIcon,
        ChevronRightIcon,
        ChevronDownIcon,
        UserGroupIcon,
        BuildingOfficeIcon,
        ChartBarIcon,
        ClipboardDocumentListIcon,
        BoltIcon,
        Squares2X2Icon,
        Cog6ToothIcon,
        QuestionMarkCircleIcon
      } = HeroIcons;
      
      icons.Bars3Icon = Bars3Icon;
      icons.PencilIcon = PencilIcon;
      icons.TrashIcon = TrashIcon;
      icons.EyeIcon = EyeIcon;
      icons.EyeSlashIcon = EyeSlashIcon;
      icons.ChevronRightIcon = ChevronRightIcon;
      icons.ChevronDownIcon = ChevronDownIcon;
      icons.UserGroupIcon = UserGroupIcon;
      icons.BuildingOfficeIcon = BuildingOfficeIcon;
      icons.ChartBarIcon = ChartBarIcon;
      icons.ClipboardDocumentListIcon = ClipboardDocumentListIcon;
      icons.BoltIcon = BoltIcon;
      icons.Squares2X2Icon = Squares2X2Icon;
      icons.Cog6ToothIcon = Cog6ToothIcon;
      icons.QuestionMarkCircleIcon = QuestionMarkCircleIcon;
      
      if (shouldLogIconDebug()) {
        console.log('[iconMapping] Added fallback icons:', Object.keys(icons).length);
      }
    } catch (error) {
      console.error('[iconMapping] Fallback icon import failed:', error);
    }
  }
  
  return icons;
};

// Dynamic icon mapping with all available icons
export const iconMapping = getAllIcons();
if (shouldLogIconDebug()) {
  console.log('[iconMapping] Loaded icons:', Object.keys(iconMapping).length);
}

// Auto-categorize icons based on their names
const categorizeIcons = () => {
  const categories = {};
  const iconNames = Object.keys(iconMapping);
  
  // Define comprehensive category patterns based on Heroicons naming
  const categoryPatterns = {
    arrows: /^Arrow|Chevron.*(?:Up|Down|Left|Right)|Pointing/i,
    navigation: /^(Bars|Ellipsis|List|Queue|Squares2X2).*|^(Home|Map|Globe|Compass|Location|Pin).*Icon$/i,
    documents: /^(Document|Clipboard|Folder|Archive|Paper|Book|Newspaper).*Icon$/i,
    communication: /^(Chat|Phone|Envelope|Bell|Speaker|Megaphone|Inbox|Video|Signal|Microphone|AtSymbol|Rss|Share|Wifi).*Icon$/i,
    interface: /^(Check|Exclamation|Information|Minus|NoSymbol|Plus|QuestionMark|XCircle|XMark|Adjustments).*Icon$/i,
    media: /^(Photo|Film|Musical|Camera|Play|Pause|Stop|Volume|Gif|Tv).*Icon$/i,
    users: /^(User|Face|Hand|Eye|Heart|Identification).*Icon$/i,
    technology: /^(Computer|Device|Wifi|Signal|Battery|CommandLine|Cpu|Server|Printer).*Icon$/i,
    security: /^(Lock|Key|Shield|FingerPrint).*Icon$/i,
    commerce: /^(Currency|Banknotes|Credit|Shopping|Tag|Gift|Ticket|Receipt|Wallet|Percent).*Icon$/i,
    data: /^(Chart|Presentation|Scale|TableCells|Variable).*Icon$/i,
    time: /^(Calendar|Clock).*Icon$/i,
    buildings: /^(Building|Globe).*Icon$/i,
    objects: /^(Academic|Archive|Backspace|Battery|Beaker|Bell|Bookmark|Briefcase|Bug|Cake|Calculator|Circle|Cloud|Code|Cog|Cube|Cursor|Fire|Flag|Funnel|Gift|Hand|Language|Lifebuoy|Light|Link|Newspaper|Paint|Pencil|Power|Puzzle|Qr|Radio|Rectangle|Rocket|Scissors|Sparkles|Star|Sun|Swatch|Trash|Trophy|Truck|Window|Wrench).*Icon$/i,
    typography: /^(Bold|Divide|Equals|H[123]|Hashtag|Italic|Slash|Strikethrough|Underline).*Icon$/i,
    emotions: /^(Face).*Icon$/i,
    miscellaneous: /^(Backward|Bolt|Forward|Moon|Square.*Stack).*Icon$/i
  };
  
  // Initialize categories
  Object.keys(categoryPatterns).forEach(key => {
    categories[key] = {
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
      icons: []
    };
  });
  
  // Add "all" category
  categories.all = {
    label: 'All Icons',
    icons: iconNames
  };
  
  // Categorize each icon
  iconNames.forEach(iconName => {
    let categorized = false;
    
    Object.entries(categoryPatterns).forEach(([categoryKey, pattern]) => {
      if (pattern.test(iconName)) {
        categories[categoryKey].icons.push(iconName);
        categorized = true;
      }
    });
    
    // If icon doesn't match any category, add to miscellaneous
    if (!categorized) {
      if (!categories.miscellaneous) {
        categories.miscellaneous = {
          label: 'Miscellaneous',
          icons: []
        };
      }
      categories.miscellaneous.icons.push(iconName);
    }
  });
  
  // Remove empty categories
  Object.keys(categories).forEach(key => {
    if (categories[key].icons.length === 0 && key !== 'all') {
      delete categories[key];
    }
  });
  
  // Sort icons within each category
  Object.keys(categories).forEach(key => {
    categories[key].icons.sort();
  });
  
  return categories;
};

// Dynamic icon categories
export const iconCategories = categorizeIcons();

// Function to get icon component by name with error handling
export const getIconByName = (iconName, className = "w-5 h-5") => {
  if (!iconName || typeof iconName !== 'string') {
    console.warn(`Invalid icon name: ${iconName}`);
    const fallbackIcon = iconMapping.QuestionMarkCircleIcon || Object.values(iconMapping)[0];
    return fallbackIcon ? React.createElement(fallbackIcon, { className }) : null;
  }

  const IconComponent = iconMapping[iconName];
  if (!IconComponent) {
    console.warn(`Icon not found: ${iconName}. Available icons: ${Object.keys(iconMapping).length}`);
    // Try multiple fallback options
    const fallbackIcon = iconMapping.QuestionMarkCircleIcon || 
                         iconMapping.Squares2X2Icon || 
                         iconMapping.ExclamationTriangleIcon ||
                         Object.values(iconMapping)[0];
    return fallbackIcon ? React.createElement(fallbackIcon, { className }) : null;
  }
  
  return React.createElement(IconComponent, { className });
};

// Get all available icon names
export const getAllIconNames = () => Object.keys(iconMapping).sort();

// Get icons by category
export const getIconsByCategory = (categoryKey) => {
  const category = iconCategories[categoryKey];
  return category ? category.icons : [];
};

// Search icons by name with improved search
export const searchIcons = (searchTerm) => {
  if (!searchTerm) return getAllIconNames();
  
  const term = searchTerm.toLowerCase();
  return getAllIconNames().filter(iconName => {
    const name = iconName.toLowerCase();
    // Search in the icon name and also in words within the name
    return name.includes(term) || 
           name.replace('icon', '').includes(term) ||
           iconName.split(/(?=[A-Z])/).some(word => word.toLowerCase().includes(term));
  });
};

// Get icon statistics
export const getIconStats = () => {
  const totalIcons = getAllIconNames().length;
  const categoryCounts = {};
  
  Object.entries(iconCategories).forEach(([key, category]) => {
    if (key !== 'all') {
      categoryCounts[category.label] = category.icons.length;
    }
  });
  
  return {
    totalIcons,
    categoryCounts,
    categories: Object.keys(iconCategories).length - 1 // Exclude 'all'
  };
};

// Export icon count for debugging
if (shouldLogIconDebug()) {
  console.log(`Loaded ${getAllIconNames().length} Heroicons dynamically`);
}
